/**
 * 공지 상세 — 여는 순간 읽음 처리(서버). 만료 공지도 링크로 열린다.
 *
 * 글자 크기(10-19 way 「플레이 스토어 앱에서 공지 줌아웃이 안 돼 시력 안 좋은 기사들이 보기 어렵다」):
 * 앱(WebView)은 두 손가락 확대가 꺼져 있다(1.3 부터 켬 — 그 전 버전 폰이 다수). 그래서 화면 안에 「가-·가+」를 둔다.
 * CSS `zoom` 이라 글자·그림·표가 같이 커지고 줄이 다시 감긴다(가로 스크롤 없이 읽힘). 고른 배율은 폰에 기억한다.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { driverApi, driverError, type Notice } from '../lib/driverApi';
import Screen, { ErrorBanner, Loading } from './Screen';
import NoticeBody from './NoticeBody';
import { when } from './format';

const ZOOM_KEY = 'driver.noticeZoom';
const ZOOMS = [1, 1.25, 1.5, 1.75, 2, 2.5];

function loadZoom(): number {
  try {
    const v = Number(localStorage.getItem(ZOOM_KEY));
    return ZOOMS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

export default function NoticeDetail() {
  const { id } = useParams();
  const [row, setRow] = useState<Notice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(loadZoom);

  function stepZoom(d: 1 | -1) {
    const next = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS.indexOf(zoom) + d))]!;
    setZoom(next);
    try {
      localStorage.setItem(ZOOM_KEY, String(next));
    } catch {
      /* 저장 못 해도 이번 화면은 커진다 */
    }
  }

  useEffect(() => {
    const noticeId = Number(id);
    if (!Number.isInteger(noticeId) || noticeId <= 0) {
      setError('없는 공지입니다.');
      return;
    }
    driverApi
      .getNotice({ noticeId })
      .then((r) => setRow(r.row))
      .catch((e) => setError(driverError(e, '공지를 불러오지 못했습니다.')));
  }, [id]);

  return (
    <Screen title="공지사항" back="/notices">
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {!row && !error ? (
        <Loading />
      ) : row ? (
        <article className="driver-card p-6">
          <div className="mb-4 flex items-center justify-end gap-2">
            <span className="mr-1 text-sm text-ink-soft">글자 크기</span>
            <button
              type="button"
              className="flex min-h-touch min-w-touch items-center justify-center rounded-xl border border-line text-base font-bold text-ink active:bg-canvas disabled:opacity-30"
              onClick={() => stepZoom(-1)}
              disabled={zoom === ZOOMS[0]}
              aria-label="글자 작게"
            >
              가−
            </button>
            <button
              type="button"
              className="flex min-h-touch min-w-touch items-center justify-center rounded-xl border border-line text-xl font-bold text-ink active:bg-canvas disabled:opacity-30"
              onClick={() => stepZoom(1)}
              disabled={zoom === ZOOMS[ZOOMS.length - 1]}
              aria-label="글자 크게"
            >
              가+
            </button>
          </div>
          <div style={zoom === 1 ? undefined : { zoom }}>
            <h2 className="text-xl font-bold text-ink">{row.subject || '(제목 없음)'}</h2>
            <p className="mt-1 text-sm text-ink-faint">
              {row.grp_name ? `${row.grp_name} · ` : ''}
              {when(row.created_at)}
            </p>
            <div className="mt-5">
              <NoticeBody text={row.comment} />
            </div>
          </div>
        </article>
      ) : null}
    </Screen>
  );
}
